import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Page } from "./_shared";
import { Skeleton } from "@/components/ui/skeleton";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, Legend } from "recharts";
import { Link } from "react-router-dom";
import { Briefcase, Users, Info, ExternalLink } from "lucide-react";

export default function Benchmarking() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/benchmarking").then((r) => setData(r.data)); }, []);

  if (!data) return <Skeleton className="h-96" />;

  return (
    <Page
      title="Benchmarking"
      subtitle="Compare seu desempenho com lojas do mesmo setor e os melhores do mercado."
    >
      {/* Sector + source banner */}
      <div className="p-5 rounded-xl border border-primary/30 bg-card flex items-start gap-4 flex-wrap">
        <div className="w-10 h-10 rounded-lg bg-primary/15 border border-primary/30 flex items-center justify-center shrink-0">
          <Briefcase className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1 min-w-64">
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Comparando você com</div>
          <div className="font-display text-xl font-semibold">{data.sector_label}</div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground mt-2">
            <Users className="w-3.5 h-3.5" />
            <span>{data.peer_count} {data.peer_count === 1 ? "loja" : "lojas"} no mesmo setor na plataforma</span>
          </div>
        </div>
        <Link to="/app/profile" className="text-xs text-primary hover:underline flex items-center gap-1 shrink-0">
          Trocar setor <ExternalLink className="w-3 h-3" />
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <Stat label="Você" value={data.your_score} accent />
        <Stat label="Média do setor" value={data.peer_avg} />
        <Stat label="Top do setor" value={data.industry_top} />
      </div>

      <div className="p-6 rounded-xl border border-border bg-card">
        <h3 className="font-display text-lg font-semibold mb-4">Comparativo por métrica</h3>
        <ResponsiveContainer width="100%" height={360}>
          <BarChart data={data.metrics} layout="vertical" margin={{ left: 24, right: 16 }}>
            <CartesianGrid stroke="#1A2B47" strokeDasharray="3 3" />
            <XAxis type="number" stroke="#94A3B8" fontSize={11} />
            <YAxis dataKey="metric" type="category" stroke="#94A3B8" fontSize={11} width={140} />
            <Tooltip contentStyle={{ backgroundColor: "#0B1320", border: "1px solid #1A2B47", borderRadius: 12 }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="you" name="Você" fill="#F05A3F" radius={[0, 6, 6, 0]} />
            <Bar dataKey="peers" name="Média setor" fill="#60A5FA" radius={[0, 6, 6, 0]} />
            <Bar dataKey="top" name="Top setor" fill="#34D399" radius={[0, 6, 6, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Data source explanation */}
      <div className="p-5 rounded-xl border border-border bg-card flex items-start gap-3">
        <Info className="w-4 h-4 text-primary mt-0.5 shrink-0" />
        <div>
          <div className="text-sm font-medium mb-1">Como calculamos os benchmarks</div>
          <p className="text-sm text-muted-foreground leading-relaxed">{data.data_source}</p>
          <p className="text-xs text-muted-foreground mt-3">
            Os dados são <strong className="text-foreground">anonimizados e agregados</strong> — nenhuma informação individual da sua loja é compartilhada.
            Para métricas onde "menor é melhor" (ruptura e estoque parado), a barra menor representa melhor performance.
          </p>
        </div>
      </div>
    </Page>
  );
}

const Stat = ({ label, value, accent }) => (
  <div className={`p-6 rounded-xl border bg-card ${accent ? "border-primary/40 glow-coral" : "border-border"}`}>
    <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className={`font-display text-4xl font-bold mt-3 ${accent ? "text-primary" : ""}`}>{value}</div>
  </div>
);
